import {
  __decorateClass
} from "./chunk-EUXUH3YW.js";

// src/decorators.ts
function Action(name, bodyValidation) {
  return function(target, propertyKey) {
    if (!target.constructor._actionMetadata) {
      target.constructor._actionMetadata = /* @__PURE__ */ new Map();
    }
    target.constructor._actionMetadata.set(name, {
      key: propertyKey,
      bodyValidation
    });
  };
}
function UnhandledAction() {
  return function(target, propertyKey) {
    target.constructor._unhandledActionMetadata = {
      key: propertyKey
    };
  };
}
function Request2(options, bodyValidation) {
  return function(target, propertyKey) {
    if (!target.constructor._requestMetadata) {
      target.constructor._requestMetadata = /* @__PURE__ */ new Map();
    }
    const path = options.path.startsWith("/") ? options.path : `/${options.path}`;
    const method = options.method || "GET";
    const routeKey = `${method}:${path}`;
    target.constructor._requestMetadata.set(routeKey, {
      key: propertyKey,
      path,
      method,
      bodyValidation
    });
  };
}
function Room(options) {
  return function(target) {
    target.path = options.path;
    target.prototype.maxUsers = options.maxUsers;
    target.prototype.persistState = options.persistState ?? true;
    target.prototype.throttleStorage = options.throttleStorage;
    target.prototype.throttleSync = options.throttleSync;
    target.prototype.sessionExpiryTime = options.sessionExpiryTime ?? 5 * 60 * 1e3;
    if (options.guards) {
      target["_roomGuards"] = options.guards;
    }
  };
}
function RoomGuard(guards) {
  return function(target) {
    target["_roomGuards"] = guards;
  };
}
function Guard(guards) {
  return function(target, propertyKey, descriptor) {
    if (!target.constructor["_actionGuards"]) {
      target.constructor["_actionGuards"] = /* @__PURE__ */ new Map();
    }
    if (!Array.isArray(guards)) {
      guards = [guards];
    }
    target.constructor["_actionGuards"].set(propertyKey, guards);
  };
}

// ../sync/src/utils.ts
function generateShortUUID() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let uuid = "";
  for (let i = 0; i < 8; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length);
    uuid += chars[randomIndex];
  }
  return uuid;
}

// src/storage.ts
var Storage = class {
  constructor() {
    this.memory = /* @__PURE__ */ new Map();
  }
  async put(key, value) {
    if (typeof key === "string") {
      this.memory.set(key, value);
      return;
    }
    for (const [entryKey, entryValue] of Object.entries(key)) {
      this.memory.set(entryKey, entryValue);
    }
  }
  async get(key) {
    return this.memory.get(key);
  }
  async delete(key) {
    if (Array.isArray(key)) {
      let deleted = 0;
      for (const entryKey of key) {
        if (this.memory.delete(entryKey)) {
          deleted += 1;
        }
      }
      return deleted;
    }
    return this.memory.delete(key);
  }
  async list(options) {
    if (!options?.prefix) {
      return this.memory;
    }
    return new Map(
      Array.from(this.memory.entries()).filter(
        ([key]) => String(key).startsWith(options.prefix)
      )
    );
  }
};

// src/server.ts
import { dset as dset2 } from "dset";
import z from "zod";
import {
  createStatesSnapshot,
  getByPath,
  load,
  syncClass,
  DELETE_TOKEN,
  generateShortUUID as generateShortUUID2,
  createStatesSnapshotDeep
} from "@signe/sync";

// src/utils.ts
import { dset } from "dset";
function isPromise(value) {
  return value instanceof Promise;
}
async function awaitReturn(val) {
  return isPromise(val) ? await val : val;
}
function isClass(obj) {
  return typeof obj === "function" && obj.prototype && obj.prototype.constructor === obj;
}
function throttle(func, wait) {
  let timeout = null;
  let lastArgs = null;
  return function(...args) {
    if (!timeout) {
      func(...args);
      timeout = setTimeout(() => {
        if (lastArgs) {
          func(...lastArgs);
          lastArgs = null;
        }
        timeout = null;
      }, wait);
    } else {
      lastArgs = args;
    }
  };
}
function extractParams(pattern, str) {
  const regexPattern = pattern.replace(/{(\w+)}/g, "(?<$1>[\\w-]+)");
  const regex = new RegExp(`^${regexPattern}$`);
  const match = regex.exec(str);
  if (match && match.groups) {
    return match.groups;
  } else if (pattern === str) {
    return {};
  } else {
    return null;
  }
}
function dremove(obj, keys) {
  if (typeof keys === "string") {
    keys = keys.split(".");
  }
  let i = 0;
  const l = keys.length;
  let t = obj;
  let k;
  while (i < l - 1) {
    k = keys[i++];
    if (k === "__proto__" || k === "constructor" || k === "prototype") return;
    if (typeof t[k] !== "object" || t[k] === null) return;
    t = t[k];
  }
  k = keys[i];
  if (t && typeof t === "object" && !(k === "__proto__" || k === "constructor" || k === "prototype")) {
    delete t[k];
  }
}
function buildObject(valuesMap, allMemory) {
  let memoryObj = {};
  for (let path of valuesMap.keys()) {
    const value = valuesMap.get(path);
    dset(memoryObj, path, value);
    if (value === "$delete") {
      dremove(allMemory, path);
    } else {
      dset(allMemory, path, value);
    }
  }
  return memoryObj;
}
function response(status, body) {
  return new Response(JSON.stringify(body), {
    status,
    headers: { "Content-Type": "application/json" }
  });
}

// src/request/response.ts
var ServerResponse = class {
  /**
   * Creates a new ServerResponse instance
   * @param interceptors Array of interceptor functions that can modify the response
   */
  constructor(interceptors = []) {
    this.statusCode = 200;
    this.responseBody = {};
    this.responseHeaders = {
      "Content-Type": "application/json"
    };
    this.interceptors = interceptors;
  }
  /**
   * Sets the status code for the response
   * @param code HTTP status code
   * @returns this instance for chaining
   */
  status(code) {
    this.statusCode = code;
    return this;
  }
  /**
   * Sets the response body and returns this instance (chainable method)
   * 
   * @param body Response body
   * @returns this instance for chaining
   */
  body(body) {
    this.responseBody = body;
    return this;
  }
  /**
   * Adds a header to the response
   * @param name Header name
   * @param value Header value
   * @returns this instance for chaining
   */
  header(name, value) {
    this.responseHeaders[name] = value;
    return this;
  }
  /**
   * Adds multiple headers to the response
   * @param headers Object containing headers
   * @returns this instance for chaining
   */
  setHeaders(headers) {
    this.responseHeaders = { ...this.responseHeaders, ...headers };
    return this;
  }
  /**
   * Add an interceptor to the chain
   * @param interceptor Function that takes a Response and returns a modified Response
   * @returns this instance for chaining
   */
  use(interceptor) {
    this.interceptors.push(interceptor);
    return this;
  }
  /**
   * Builds and returns the Response object after applying all interceptors
   * @returns Promise<Response> The final Response object
   * @private Internal method used by terminal methods
   */
  async buildResponse() {
    let response2 = new Response(JSON.stringify(this.responseBody), {
      status: this.statusCode,
      headers: this.responseHeaders
    });
    for (const interceptor of this.interceptors) {
      try {
        const interceptedResponse = interceptor(response2);
        if (interceptedResponse instanceof Promise) {
          response2 = await interceptedResponse;
        } else {
          response2 = interceptedResponse;
        }
      } catch (error) {
        console.error("Error in interceptor:", error);
      }
    }
    return response2;
  }
  /**
   * Sets the response body to the JSON-stringified version of the provided value
   * and sends the response (terminal method)
   * 
   * @param body Response body to be JSON stringified
   * @returns Promise<Response> The final Response object
   */
  async json(body) {
    this.responseBody = body;
    this.responseHeaders["Content-Type"] = "application/json";
    return this.buildResponse();
  }
  /**
   * Sends the response with the current configuration (terminal method)
   * 
   * @param body Optional body to set before sending
   * @returns Promise<Response> The final Response object
   */
  async send(body) {
    if (body !== void 0) {
      this.responseBody = body;
    }
    return this.buildResponse();
  }
  /**
   * Sends a plain text response (terminal method)
   * 
   * @param text Text to send
   * @returns Promise<Response> The final Response object
   */
  async text(text) {
    this.responseBody = text;
    this.responseHeaders["Content-Type"] = "text/plain";
    let response2 = new Response(text, {
      status: this.statusCode,
      headers: this.responseHeaders
    });
    for (const interceptor of this.interceptors) {
      try {
        const interceptedResponse = interceptor(response2);
        if (interceptedResponse instanceof Promise) {
          response2 = await interceptedResponse;
        } else {
          response2 = interceptedResponse;
        }
      } catch (error) {
        console.error("Error in interceptor:", error);
      }
    }
    return response2;
  }
  /**
   * Redirects to the specified URL (terminal method)
   * 
   * @param url URL to redirect to
   * @param statusCode HTTP status code (default: 302)
   * @returns Promise<Response> The final Response object
   */
  async redirect(url, statusCode = 302) {
    this.statusCode = statusCode;
    this.responseHeaders["Location"] = url;
    return this.buildResponse();
  }
  /**
   * Creates a success response with status 200
   * @param body Response body
   * @returns Promise<Response> The final Response object
   */
  async success(body = {}) {
    return this.status(200).json(body);
  }
  /**
   * Creates an error response with status 400
   * @param message Error message
   * @param details Additional error details
   * @returns Promise<Response> The final Response object
   */
  async badRequest(message, details = {}) {
    return this.status(400).json({
      error: message,
      ...details
    });
  }
  /**
   * Creates an error response with status 403
   * @param message Error message
   * @returns Promise<Response> The final Response object
   */
  async notPermitted(message = "Not permitted") {
    return this.status(403).json({ error: message });
  }
  /**
   * Creates an error response with status 401
   * @param message Error message
   * @returns Promise<Response> The final Response object
   */
  async unauthorized(message = "Unauthorized") {
    return this.status(401).json({ error: message });
  }
  /**
   * Creates an error response with status 404
   * @param message Error message
   * @returns Promise<Response> The final Response object
   */
  async notFound(message = "Not found") {
    return this.status(404).json({ error: message });
  }
  /**
   * Creates an error response with status 500
   * @param message Error message
   * @returns Promise<Response> The final Response object
   */
  async serverError(message = "Internal Server Error") {
    return this.status(500).json({ error: message });
  }
};

// src/request/cors.ts
function cors(res, options = {}) {
  const newHeaders = new Headers(res.headers);
  const requestOrigin = options.origin || "*";
  newHeaders.set("Access-Control-Allow-Origin", requestOrigin);
  if (options.credentials) {
    newHeaders.set("Access-Control-Allow-Credentials", "true");
  }
  if (options.exposedHeaders && options.exposedHeaders.length) {
    newHeaders.set("Access-Control-Expose-Headers", options.exposedHeaders.join(", "));
  }
  if (options.methods && options.methods.length) {
    newHeaders.set("Access-Control-Allow-Methods", options.methods.join(", "));
  } else {
    newHeaders.set("Access-Control-Allow-Methods", "GET, POST, PUT, DELETE, PATCH, OPTIONS");
  }
  if (options.allowedHeaders && options.allowedHeaders.length) {
    newHeaders.set("Access-Control-Allow-Headers", options.allowedHeaders.join(", "));
  } else {
    newHeaders.set("Access-Control-Allow-Headers", "Content-Type, Authorization, X-Requested-With");
  }
  if (options.maxAge) {
    newHeaders.set("Access-Control-Max-Age", options.maxAge.toString());
  } else {
    newHeaders.set("Access-Control-Max-Age", "86400");
  }
  return new Response(res.body, {
    status: res.status,
    headers: newHeaders
  });
}
function createCorsInterceptor(options = {}) {
  return (res) => cors(res, options);
}

// src/server.ts
var Message = z.object({
  action: z.string(),
  value: z.any()
});
var STATE_PREFIX = "state:";
var SESSION_PREFIX = "session:";
var SESSION_PUBLIC_PREFIX = "session-public:";
var TRANSFER_PREFIX = "transfer:";
var INTERNAL_PREFIX = "$room:";
var SESSION_GC_LAST_RUN_KEY = `${INTERNAL_PREFIX}session-gc:last-run`;
var TRANSFER_GC_LAST_RUN_KEY = `${INTERNAL_PREFIX}transfer-gc:last-run`;
var DEFAULT_TRANSFER_EXPIRY_MS = 5 * 60 * 1e3;
var Server = class {
  /**
   * @constructor
   * @param {Party.Room} room - The room object representing the current game or application instance.
   * 
   * @example
   * ```typescript
   * const server = new MyServer(new ServerIo("game"));
   * ```
   */
  constructor(room) {
    this.room = room;
    this.subRoom = null;
    this.rooms = [];
  }
  /**
    * @readonly
    * @property {boolean} isHibernate - Indicates whether the server is in hibernate mode.
    * 
    * @example
    * ```typescript
    * if (!server.isHibernate) {
    *   console.log("Server is active");
    * }
    * ```
    */
  get isHibernate() {
    return !!this["options"]?.hibernate;
  }
  get roomStorage() {
    return this.room.storage;
  }
  stateKey(path) {
    return `${STATE_PREFIX}${path}`;
  }
  sessionKey(privateId) {
    return `${SESSION_PREFIX}${privateId}`;
  }
  sessionPublicKey(publicId) {
    return `${SESSION_PUBLIC_PREFIX}${publicId}`;
  }
  transferKey(token) {
    return `${TRANSFER_PREFIX}${token}`;
  }
  isInternalStorageKey(key) {
    return key.startsWith(STATE_PREFIX) || key.startsWith(SESSION_PREFIX) || key.startsWith(SESSION_PUBLIC_PREFIX) || key.startsWith(TRANSFER_PREFIX) || key.startsWith(INTERNAL_PREFIX);
  }
  async listStorage(prefix) {
    if (!prefix) {
      return this.room.storage.list();
    }
    return this.room.storage.list({ prefix });
  }
  async loadStatePath(path) {
    return this.room.storage.get(this.stateKey(path));
  }
  async saveStatePath(path, value) {
    await this.room.storage.put(this.stateKey(path), value);
  }
  async putStorageEntries(entries) {
    const keys = Object.keys(entries);
    if (keys.length === 0) return;
    if (keys.length === 1) {
      const key = keys[0];
      await this.room.storage.put(key, entries[key]);
      return;
    }
    await this.room.storage.put(entries);
  }
  async deleteStorageKeys(keys) {
    const uniqueKeys = Array.from(new Set(keys));
    if (uniqueKeys.length === 0) return;
    if (uniqueKeys.length === 1) {
      await this.room.storage.delete(uniqueKeys[0]);
      return;
    }
    await this.room.storage.delete(uniqueKeys);
  }
  async deleteStatePath(path) {
    const stateKey = this.stateKey(path);
    const descendantEntries = await this.listStorage(`${stateKey}.`);
    await this.deleteStorageKeys([
      ...Array.from(descendantEntries.keys()),
      stateKey
    ]);
    await this.saveStatePath(path, DELETE_TOKEN);
  }
  containsDeleteToken(value) {
    if (value === DELETE_TOKEN) {
      return true;
    }
    if (!value || typeof value !== "object") {
      return false;
    }
    if (Array.isArray(value)) {
      return value.some((item) => this.containsDeleteToken(item));
    }
    return Object.values(value).some((item) => this.containsDeleteToken(item));
  }
  async compactStateStorage(instance) {
    const entries = await this.listStorage(STATE_PREFIX);
    const keys = Array.from(entries.keys());
    if (keys.length === 0) {
      return;
    }
    const snapshot = createStatesSnapshotDeep(instance);
    await this.deleteStorageKeys(keys);
    await this.saveStatePath(".", snapshot);
    const metrics = instance.$storageMetrics;
    if (metrics) {
      metrics.stateCompactions += 1;
      metrics.stateCompactionDeletes += keys.length;
    }
  }
  createUserFromClassType(classType, ...args) {
    if (!classType) {
      return void 0;
    }
    return isClass(classType) ? new classType() : classType(...args);
  }
  async restoreUsersStorageSnapshot(instance, snapshot, options) {
    if (!snapshot || typeof snapshot !== "object" || Array.isArray(snapshot)) {
      return snapshot;
    }
    const restoreUser = instance["onUserStorageRestore"];
    if (typeof restoreUser !== "function") {
      return snapshot;
    }
    const signal2 = this.getUsersProperty(instance);
    const usersPropName = this.getUsersPropName(instance);
    if (!signal2 || !usersPropName) {
      return snapshot;
    }
    const usersSnapshot = snapshot[usersPropName];
    if (!usersSnapshot || typeof usersSnapshot !== "object" || Array.isArray(usersSnapshot)) {
      return snapshot;
    }
    const { classType } = signal2.options || {};
    let nextSnapshot = snapshot;
    let nextUsersSnapshot = usersSnapshot;
    for (const [publicId, userSnapshot] of Object.entries(usersSnapshot)) {
      const user = this.createUserFromClassType(classType, publicId);
      const restoredUserSnapshot = await awaitReturn(
        restoreUser.call(instance, {
          userSnapshot,
          user,
          publicId,
          usersPropName,
          room: this.room,
          server: this,
          legacy: options.legacy
        })
      );
      if (restoredUserSnapshot !== void 0 && restoredUserSnapshot !== userSnapshot) {
        if (nextSnapshot === snapshot) {
          nextSnapshot = { ...snapshot };
        }
        if (nextUsersSnapshot === usersSnapshot) {
          nextUsersSnapshot = { ...usersSnapshot };
          nextSnapshot[usersPropName] = nextUsersSnapshot;
        }
        nextUsersSnapshot[publicId] = restoredUserSnapshot;
      }
    }
    return nextSnapshot;
  }
  async restoreStorageSnapshot(instance, snapshot, options) {
    const restoreSnapshot = instance["onStorageRestore"];
    let restoredSnapshot = snapshot;
    if (typeof restoreSnapshot === "function") {
      const result = await awaitReturn(
        restoreSnapshot.call(instance, {
          snapshot,
          room: this.room,
          server: this,
          legacy: options.legacy
        })
      );
      if (result !== void 0) {
        restoredSnapshot = result;
      }
    }
    return this.restoreUsersStorageSnapshot(instance, restoredSnapshot, options);
  }
  createStorageMetrics() {
    return {
      loadMs: 0,
      loadStateKeys: 0,
      loadLegacyKeys: 0,
      stateCompactions: 0,
      stateCompactionDeletes: 0,
      persistFlushes: 0,
      persistWrites: 0,
      persistDeletes: 0,
      persistLastFlushMs: 0,
      sessionGcRuns: 0,
      sessionGcScanned: 0,
      sessionGcExpired: 0,
      sessionIndexRepairs: 0,
      transferGcRuns: 0,
      transferGcScanned: 0,
      transferGcExpired: 0
    };
  }
  getTransferExpiryTime(subRoom) {
    return subRoom?.transferExpiryTime ?? subRoom?.constructor?.prototype?.transferExpiryTime ?? subRoom?.constructor?.transferExpiryTime ?? DEFAULT_TRANSFER_EXPIRY_MS;
  }
  getPrivateId(conn) {
    return conn.state?.privateId || conn.sessionId || conn.id;
  }
  hasActiveSessionConnection(privateId) {
    return Array.from(this.room.getConnections()).some((conn) => this.getPrivateId(conn) === privateId);
  }
  async send(conn, obj, subRoom) {
    obj = structuredClone(obj);
    if (subRoom.interceptorPacket) {
      const signal2 = this.getUsersProperty(subRoom);
      const { publicId } = conn.state;
      const user = signal2?.()[publicId];
      obj = await awaitReturn(subRoom["interceptorPacket"]?.(user, obj, conn));
      if (obj === null) return;
    }
    conn.send(JSON.stringify(obj));
  }
  broadcast(obj, subRoom) {
    for (let conn of this.room.getConnections()) {
      this.send(conn, obj, subRoom);
    }
  }
  /**
   * @method onStart
   * @async
   * @description Initializes the server and creates the initial room if not in hibernate mode.
   * @returns {Promise<void>}
   * 
   * @example
   * ```typescript
   * async function initServer() {
   *   await server.onStart();
   *   console.log("Server started");
   * }
   * ```
   */
  async onStart() {
    if (!this.isHibernate) {
      this.subRoom = await this.createRoom();
    }
  }
  async runGarbageCollector() {
    await this.garbageCollector({ sessionExpiryTime: -1 });
  }
  async garbageCollector(options) {
    const subRoom = await this.getSubRoom();
    if (!subRoom) return;
    const SESSION_EXPIRY_TIME = Number(options.sessionExpiryTime);
    if (!Number.isFinite(SESSION_EXPIRY_TIME)) {
      return;
    }
    const activeConnections = [...this.room.getConnections()];
    const activePrivateIds = new Set(activeConnections.map((conn) => this.getPrivateId(conn)));
    try {
      const sessions = await this.listStorage(SESSION_PREFIX);
      const users = this.getUsersProperty(subRoom);
      const usersPropName = this.getUsersPropName(subRoom);
      const metrics = subRoom.$storageMetrics;
      if (metrics) {
        metrics.sessionGcRuns += 1;
        metrics.sessionGcScanned += sessions.size;
      }
      const validPublicIds = /* @__PURE__ */ new Set();
      const expiredPublicIds = /* @__PURE__ */ new Set();
      const now = Date.now();
      for (const [key, session] of sessions) {
        if (!key.startsWith(SESSION_PREFIX)) continue;
        const privateId = key.slice(SESSION_PREFIX.length);
        const typedSession = session;
        if (!activePrivateIds.has(privateId) && !typedSession.connected && typedSession.disconnectedAt !== void 0 && now - typedSession.disconnectedAt >= SESSION_EXPIRY_TIME) {
          await this.deleteSession(privateId);
          expiredPublicIds.add(typedSession.publicId);
          if (metrics) {
            metrics.sessionGcExpired += 1;
          }
        } else if (typedSession && typedSession.publicId) {
          validPublicIds.add(typedSession.publicId);
        }
      }
      await this.repairSessionPublicIndexes(sessions, metrics);
      if (users && usersPropName) {
        const currentUsers = users();
        for (const publicId in currentUsers) {
          if (expiredPublicIds.has(publicId) && !validPublicIds.has(publicId)) {
            delete currentUsers[publicId];
          }
        }
      }
    } catch (error) {
      console.error("Error in garbage collector:", error);
    }
  }
  scheduleSessionGarbageCollector(sessionExpiryTime, privateId) {
    const normalizedSessionExpiryTime = Number(sessionExpiryTime);
    if (!Number.isFinite(normalizedSessionExpiryTime) || normalizedSessionExpiryTime < 0) {
      return;
    }
    setTimeout(() => {
      if (privateId) {
        void this.expireDisconnectedSession(privateId, normalizedSessionExpiryTime);
        return;
      }
      void this.garbageCollector({ sessionExpiryTime: normalizedSessionExpiryTime });
    }, normalizedSessionExpiryTime);
  }
  getSessionExpiryTime(subRoom) {
    return subRoom?.sessionExpiryTime ?? subRoom?.constructor?.prototype?.sessionExpiryTime ?? subRoom?.constructor?.sessionExpiryTime;
  }
  async shouldRunSessionGarbageCollector(sessionExpiryTime) {
    const normalizedSessionExpiryTime = Number(sessionExpiryTime);
    if (!Number.isFinite(normalizedSessionExpiryTime) || normalizedSessionExpiryTime < 0) {
      return false;
    }
    const now = Date.now();
    const lastRun = await this.room.storage.get(SESSION_GC_LAST_RUN_KEY);
    if (lastRun && now - lastRun < normalizedSessionExpiryTime) {
      return false;
    }
    await this.room.storage.put(SESSION_GC_LAST_RUN_KEY, now);
    return true;
  }
  async shouldRunInterval(key, interval) {
    const normalizedInterval = Number(interval);
    if (!Number.isFinite(normalizedInterval) || normalizedInterval < 0) {
      return false;
    }
    const now = Date.now();
    const lastRun = await this.room.storage.get(key);
    if (lastRun && now - lastRun < normalizedInterval) {
      return false;
    }
    await this.room.storage.put(key, now);
    return true;
  }
  async repairSessionPublicIndexes(sessions, metrics) {
    const sessionEntries = sessions ?? await this.listStorage(SESSION_PREFIX);
    const expected = /* @__PURE__ */ new Map();
    for (const [key, session] of sessionEntries) {
      if (!session?.publicId) continue;
      const privateId = key.slice(SESSION_PREFIX.length);
      const privateIds = expected.get(session.publicId) ?? [];
      privateIds.push(privateId);
      expected.set(session.publicId, privateIds);
    }
    const publicIndexes = await this.listStorage(SESSION_PUBLIC_PREFIX);
    const writes = {};
    const deletes = [];
    let repairs = 0;
    for (const [publicId, privateIds] of expected) {
      privateIds.sort();
      const key = this.sessionPublicKey(publicId);
      const existing = publicIndexes.get(key) ?? [];
      const normalizedExisting = Array.isArray(existing) ? [...existing].sort() : [];
      if (JSON.stringify(normalizedExisting) !== JSON.stringify(privateIds)) {
        writes[key] = privateIds;
        repairs += 1;
      }
    }
    for (const [key] of publicIndexes) {
      const publicId = key.slice(SESSION_PUBLIC_PREFIX.length);
      if (!expected.has(publicId)) {
        deletes.push(key);
        repairs += 1;
      }
    }
    await Promise.all([
      this.putStorageEntries(writes),
      this.deleteStorageKeys(deletes)
    ]);
    if (metrics) {
      metrics.sessionIndexRepairs += repairs;
    }
  }
  isTransferExpired(transfer, transferExpiryTime) {
    if (!transfer) return true;
    if (!Number.isFinite(transferExpiryTime) || transferExpiryTime < 0) {
      return false;
    }
    const created = Number(transfer.created);
    if (!Number.isFinite(created)) {
      return false;
    }
    return Date.now() - created >= transferExpiryTime;
  }
  async cleanupExpiredTransfers(transferExpiryTime, metrics) {
    if (!Number.isFinite(transferExpiryTime) || transferExpiryTime < 0) {
      return;
    }
    const transfers = await this.listStorage(TRANSFER_PREFIX);
    const expiredKeys = [];
    for (const [key, transfer] of transfers) {
      if (this.isTransferExpired(transfer, transferExpiryTime)) {
        expiredKeys.push(key);
      }
    }
    await this.deleteStorageKeys(expiredKeys);
    if (metrics) {
      metrics.transferGcRuns += 1;
      metrics.transferGcScanned += transfers.size;
      metrics.transferGcExpired += expiredKeys.length;
    }
  }
  async expireDisconnectedSession(privateId, sessionExpiryTime) {
    const session = await this.getSession(privateId);
    if (!session || session.connected || session.disconnectedAt === void 0) {
      return;
    }
    if (this.hasActiveSessionConnection(privateId)) {
      return;
    }
    const elapsed = Date.now() - session.disconnectedAt;
    if (elapsed < sessionExpiryTime) {
      setTimeout(() => {
        void this.expireDisconnectedSession(privateId, sessionExpiryTime);
      }, sessionExpiryTime - elapsed);
      return;
    }
    await this.deleteSession(privateId);
    const privateIds = await this.getSessionPrivateIds(session.publicId);
    for (const otherPrivateId of privateIds) {
      const otherSession = await this.getSession(otherPrivateId);
      if (otherSession?.publicId === session.publicId) {
        return;
      }
    }
    const subRoom = await this.getSubRoom();
    const users = this.getUsersProperty(subRoom);
    if (users?.()[session.publicId]) {
      delete users()[session.publicId];
    }
  }
  /**
   * @method createRoom
   * @private
   * @async
   * @param {CreateRoomOptions} [options={}] - Options for creating the room.
   * @returns {Promise<Object>} The created room instance.
   * 
   * @example
   * ```typescript
   * // This method is private and called internally
   * async function internalCreateRoom() {
   *   const room = await this.createRoom({ getMemoryAll: true });
   *   console.log("Room created:", room);
   * }
   * ```
   */
  async createRoom(options = {}) {
    let instance;
    let init = true;
    let initPersist = true;
    for (let room of this.rooms) {
      const params = extractParams(room.path, this.room.id);
      if (params) {
        instance = new room(this.room, params);
        break;
      }
    }
    if (!instance) {
      return null;
    }
    const loadMemory = async () => {
      const startedAt = Date.now();
      const metrics = instance.$storageMetrics;
      const root = await this.loadStatePath(".");
      const memory = await this.listStorage(STATE_PREFIX);
      metrics.loadStateKeys = memory.size;
      const tmpObject = root || {};
      for (let [storageKey, value] of memory) {
        const key = storageKey.slice(STATE_PREFIX.length);
        if (key === ".") {
          continue;
        }
        dset2(tmpObject, key, value);
      }
      if (root === void 0 && memory.size === 0) {
        const legacyRoot = await this.room.storage.get(".");
        const legacyMemory = await this.room.storage.list();
        const legacyObject = legacyRoot || {};
        const migratedEntries = [];
        const legacyDeleteKeys = [];
        if (legacyRoot !== void 0) {
          migratedEntries.push([".", legacyRoot]);
          legacyDeleteKeys.push(".");
        }
        for (let [key, value] of legacyMemory) {
          if (key === "." || this.isInternalStorageKey(key)) {
            continue;
          }
          dset2(legacyObject, key, value);
          migratedEntries.push([key, value]);
          legacyDeleteKeys.push(key);
        }
        metrics.loadLegacyKeys = migratedEntries.length;
        await this.putStorageEntries(
          Object.fromEntries(
            migratedEntries.map(([path, value]) => [this.stateKey(path), value])
          )
        );
        if (legacyDeleteKeys.length > 0) {
          await this.deleteStorageKeys(legacyDeleteKeys);
        }
        const restoredLegacyObject = await this.restoreStorageSnapshot(instance, legacyObject, {
          legacy: true
        });
        load(instance, restoredLegacyObject, true);
        if (this.containsDeleteToken(restoredLegacyObject)) {
          await this.compactStateStorage(instance);
        }
        metrics.loadMs = Date.now() - startedAt;
        return;
      }
      const restoredObject = await this.restoreStorageSnapshot(instance, tmpObject, {
        legacy: false
      });
      load(instance, restoredObject, true);
      if (this.containsDeleteToken(restoredObject)) {
        await this.compactStateStorage(instance);
      }
      metrics.loadMs = Date.now() - startedAt;
    };
    instance.$memoryAll = {};
    instance.$storageMetrics = this.createStorageMetrics();
    const shouldPersistState = instance["persistState"] !== false;
    instance.$autoSync = instance["autoSync"] !== false;
    instance.$pendingSync = /* @__PURE__ */ new Map();
    instance.$pendingInitialSync = /* @__PURE__ */ new Map();
    instance.$send = (conn, obj) => {
      return this.send(conn, obj, instance);
    };
    instance.$broadcast = (obj) => {
      return this.broadcast(obj, instance);
    };
    instance.$applySync = () => {
      let packet;
      if (instance.$pendingSync.size > 0) {
        if (options.getMemoryAll) {
          buildObject(instance.$pendingSync, instance.$memoryAll);
        }
        packet = buildObject(instance.$pendingSync, instance.$memoryAll);
        instance.$pendingSync.clear();
      } else {
        packet = instance.$memoryAll;
      }
      const pendingConnections = new Set(instance.$pendingInitialSync.keys());
      for (const [conn, publicId] of instance.$pendingInitialSync) {
        this.send(conn, {
          type: "sync",
          value: {
            pId: publicId,
            ...packet
          }
        }, instance);
      }
      instance.$pendingInitialSync.clear();
      for (const conn of this.room.getConnections()) {
        if (!pendingConnections.has(conn)) {
          this.send(conn, {
            type: "sync",
            value: packet
          }, instance);
        }
      }
    };
    instance.$sessionTransfer = async (conn, targetRoomId) => {
      let user;
      const signal2 = this.getUsersProperty(instance);
      if (!signal2) {
        console.error("[sessionTransfer] `users` property not defined in the room.");
        return null;
      }
      const { publicId } = conn.state;
      user = signal2()[publicId];
      if (!user) {
        console.error(`[sessionTransfer] User with publicId ${publicId} not found.`);
        return null;
      }
      const sessionEntry = await this.getSessionEntryByPublicId(publicId);
      const userSession = sessionEntry?.session;
      const privateId = sessionEntry?.privateId ?? null;
      if (!userSession || !privateId) {
        console.error(`[sessionTransfer] Session for publicId ${publicId} not found.`);
        return null;
      }
      const usersPropName = this.getUsersPropName(instance);
      if (!usersPropName) {
        console.error("[sessionTransfer] `users` property not defined in the room.");
        return null;
      }
      const userSnapshot = createStatesSnapshotDeep(user);
      const transferData = {
        privateId,
        userSnapshot,
        sessionState: userSession.state,
        publicId
      };
      try {
        const targetRoomParty = await this.room.context.parties.main.get(targetRoomId);
        const response2 = await targetRoomParty.fetch("/session-transfer", {
          method: "POST",
          body: JSON.stringify(transferData),
          headers: {
            "Content-Type": "application/json"
          }
        });
        if (!response2.ok) {
          throw new Error(`Transfer request failed: ${await response2.text()}`);
        }
        const { transferToken } = await response2.json();
        return transferToken;
      } catch (error) {
        console.error(`[sessionTransfer] Failed to transfer session to room ${targetRoomId}:`, error);
        return null;
      }
    };
    const syncCb = (values) => {
      if (options.getMemoryAll) {
        buildObject(values, instance.$memoryAll);
      }
      if (init && this.isHibernate) {
        init = false;
        return;
      }
      if (!instance.$autoSync) {
        for (const [path, value] of values) {
          instance.$pendingSync.set(path, value);
        }
        values.clear();
        return;
      }
      const packet = buildObject(values, instance.$memoryAll);
      this.broadcast(
        {
          type: "sync",
          value: packet
        },
        instance
      );
      values.clear();
    };
    const flushPersist = async (values) => {
      const startedAt = Date.now();
      const stateWrites = {};
      const deleteTasks = [];
      let hasDeletes = false;
      for (let [path, value] of values) {
        const _instance = path == "." ? instance : getByPath(instance, path);
        if (value == DELETE_TOKEN) {
          hasDeletes = true;
          deleteTasks.push(this.deleteStatePath(path));
        } else {
          const itemValue = _instance?.$snapshot ? createStatesSnapshot(_instance) : value;
          stateWrites[this.stateKey(path)] = itemValue;
        }
      }
      await Promise.all([
        this.putStorageEntries(stateWrites),
        ...deleteTasks
      ]);
      if (hasDeletes) {
        await this.compactStateStorage(instance);
      }
      const metrics = instance.$storageMetrics;
      if (metrics) {
        metrics.persistFlushes += 1;
        metrics.persistWrites += Object.keys(stateWrites).length;
        metrics.persistDeletes += deleteTasks.length;
        metrics.persistLastFlushMs = Date.now() - startedAt;
      }
    };
    let persistQueue = Promise.resolve();
    const persistCb = async (values) => {
      if (initPersist) {
        values.clear();
        return;
      }
      const valuesSnapshot = new Map(values);
      values.clear();
      persistQueue = persistQueue.then(
        () => flushPersist(valuesSnapshot),
        () => flushPersist(valuesSnapshot)
      );
      await persistQueue;
    };
    const debouncePersist = (wait) => {
      let timeout = null;
      let flushing = false;
      const pending = /* @__PURE__ */ new Map();
      const schedule = () => {
        if (timeout) {
          clearTimeout(timeout);
        }
        timeout = setTimeout(() => {
          void flush();
        }, wait);
      };
      const flush = async () => {
        timeout = null;
        if (flushing) {
          schedule();
          return;
        }
        const values = new Map(pending);
        pending.clear();
        if (!values.size) {
          return;
        }
        flushing = true;
        try {
          await persistCb(values);
        } finally {
          flushing = false;
          if (pending.size) {
            schedule();
          }
        }
      };
      return (values) => {
        if (initPersist) {
          values.clear();
          return;
        }
        for (const [path, value] of values) {
          pending.set(path, value);
        }
        values.clear();
        schedule();
      };
    };
    const disabledPersist = (values) => {
      values.clear();
    };
    syncClass(instance, {
      onSync: instance["throttleSync"] ? throttle(syncCb, instance["throttleSync"]) : syncCb,
      onPersist: shouldPersistState ? instance["throttleStorage"] ? debouncePersist(instance["throttleStorage"]) : persistCb : disabledPersist
    });
    if (shouldPersistState) {
      await loadMemory();
    }
    initPersist = false;
    init = false;
    return instance;
  }
  /**
   * @method getSubRoom
   * @private
   * @async
   * @param {Object} [options={}] - Options for getting the sub-room.
   * @returns {Promise<Object>} The sub-room instance.
   * 
   * @example
   * ```typescript
   * // This method is private and called internally
   * async function internalGetSubRoom() {
   *   const subRoom = await this.getSubRoom();
   *   console.log("Sub-room retrieved:", subRoom);
   * }
   * ```
   */
  async getSubRoom(options = {}) {
    let subRoom;
    if (this.isHibernate) {
      subRoom = await this.createRoom(options);
    } else {
      subRoom = this.subRoom;
    }
    return subRoom;
  }
  /**
   * @method getUsersProperty
   * @private
   * @param {Object} subRoom - The sub-room instance.
   * @returns {Object|null} The users property of the sub-room, or null if not found.
   * 
   * @example
   * ```typescript
   * // This method is private and called internally
   * function internalGetUsers(subRoom) {
   *   const users = this.getUsersProperty(subRoom);
   *   console.log("Users:", users);
   * }
   * ```
   */
  getUsersProperty(subRoom) {
    const meta = subRoom.constructor["_propertyMetadata"];
    const propId = meta?.get("users");
    if (propId) {
      return subRoom[propId];
    }
    return null;
  }
  getUsersPropName(subRoom) {
    if (!subRoom) return null;
    const metadata = subRoom.constructor._propertyMetadata;
    if (!metadata) return null;
    return metadata.get("users");
  }
  /**
   * Retrieves the connection status property from a user object.
   * 
   * @param {any} user - The user object to get the connection property from.
   * @returns {Function|null} - The connection property signal function or null if not found.
   * @private
   */
  getUserConnectionProperty(user) {
    if (!user) return null;
    const metadata = user.constructor._propertyMetadata;
    if (!metadata) return null;
    const connectedPropName = metadata.get("connected");
    if (!connectedPropName) return null;
    return user[connectedPropName];
  }
  /**
   * Updates a user's connection status in the signal.
   * 
   * @param {any} user - The user object to update.
   * @param {boolean} isConnected - The new connection status.
   * @returns {boolean} - Whether the update was successful.
   * @private
   */
  updateUserConnectionStatus(user, isConnected) {
    const connectionSignal = this.getUserConnectionProperty(user);
    if (connectionSignal) {
      connectionSignal.set(isConnected);
      return true;
    }
    return false;
  }
  /**
   * @method getSession
   * @private
   * @param {string} privateId - The private ID of the session.
   * @returns {Promise<Object|null>} The session object, or null if not found.
   * 
   * @example
   * ```typescript
   * const session = await server.getSession("privateId");
   * console.log(session);
   * ```
   */
  async getSession(privateId) {
    if (!privateId) return null;
    try {
      const session = await this.room.storage.get(this.sessionKey(privateId));
      return session;
    } catch (e) {
      return null;
    }
  }
  async getSessionPrivateIds(publicId) {
    if (!publicId) return [];
    const privateIds = await this.room.storage.get(this.sessionPublicKey(publicId));
    return Array.isArray(privateIds) ? privateIds : [];
  }
  async saveSessionPrivateIds(publicId, privateIds) {
    const key = this.sessionPublicKey(publicId);
    if (privateIds.length === 0) {
      await this.room.storage.delete(key);
      return;
    }
    await this.room.storage.put(key, privateIds);
  }
  async addSessionToPublicIndex(privateId, publicId) {
    const privateIds = await this.getSessionPrivateIds(publicId);
    if (privateIds.includes(privateId)) {
      return;
    }
    await this.saveSessionPrivateIds(publicId, [...privateIds, privateId]);
  }
  async removeSessionFromPublicIndex(privateId, publicId) {
    const privateIds = await this.getSessionPrivateIds(publicId);
    await this.saveSessionPrivateIds(
      publicId,
      privateIds.filter((id2) => id2 !== privateId)
    );
  }
  async getSessionEntryByPublicId(publicId) {
    const indexedPrivateIds = await this.getSessionPrivateIds(publicId);
    const stalePrivateIds = [];
    for (const privateId of indexedPrivateIds) {
      const session = await this.getSession(privateId);
      if (session?.publicId === publicId) {
        return { privateId, session };
      }
      stalePrivateIds.push(privateId);
    }
    if (stalePrivateIds.length) {
      await this.saveSessionPrivateIds(
        publicId,
        indexedPrivateIds.filter((id2) => !stalePrivateIds.includes(id2))
      );
    }
    const sessions = await this.listStorage(SESSION_PREFIX);
    for (const [key, session] of sessions) {
      const privateId = key.slice(SESSION_PREFIX.length);
      if (session?.publicId) {
        await this.addSessionToPublicIndex(privateId, session.publicId);
      }
      if (session?.publicId === publicId) {
        return { privateId, session };
      }
    }
    return null;
  }
  async saveSession(privateId, data) {
    const existingSession = await this.getSession(privateId);
    const sessionData = {
      ...data,
      created: data.created || Date.now(),
      connected: data.connected !== void 0 ? data.connected : true
    };
    await this.room.storage.put(this.sessionKey(privateId), sessionData);
    if (existingSession?.publicId && existingSession.publicId !== sessionData.publicId) {
      await this.removeSessionFromPublicIndex(privateId, existingSession.publicId);
    }
    await this.addSessionToPublicIndex(privateId, sessionData.publicId);
  }
  async updateSessionConnection(privateId, connected) {
    const session = await this.getSession(privateId);
    if (session) {
      const nextSession = { ...session, connected };
      if (connected) {
        delete nextSession.disconnectedAt;
      } else {
        nextSession.disconnectedAt = Date.now();
      }
      if (!await this.getSession(privateId)) {
        return;
      }
      await this.saveSession(privateId, nextSession);
    }
  }
  /**
   * @method deleteSession
   * @private
   * @param {string} privateId - The private ID of the session to delete.
   * @returns {Promise<void>}
   * 
   * @example
   * ```typescript
   * await server.deleteSession("privateId");
   * ```
   */
  async deleteSession(privateId) {
    const session = await this.getSession(privateId);
    await this.room.storage.delete(this.sessionKey(privateId));
    if (session?.publicId) {
      await this.removeSessionFromPublicIndex(privateId, session.publicId);
    }
  }
  async onConnectClient(conn, ctx) {
    const subRoom = await this.getSubRoom({
      getMemoryAll: true
    });
    if (!subRoom) {
      conn.close();
      return;
    }
    const sessionExpiryTime = this.getSessionExpiryTime(subRoom);
    if (await this.shouldRunSessionGarbageCollector(sessionExpiryTime)) {
      await this.garbageCollector({ sessionExpiryTime });
    }
    const transferExpiryTime = this.getTransferExpiryTime(subRoom);
    if (await this.shouldRunInterval(TRANSFER_GC_LAST_RUN_KEY, transferExpiryTime)) {
      await this.cleanupExpiredTransfers(
        transferExpiryTime,
        subRoom.$storageMetrics
      );
    }
    const roomGuards = subRoom.constructor["_roomGuards"] || [];
    for (const guard of roomGuards) {
      const isAuthorized = await guard(conn, ctx, this.room);
      if (!isAuthorized) {
        conn.close();
        return;
      }
    }
    let transferToken = null;
    if (ctx.request?.url) {
      const url = new URL(ctx.request.url);
      transferToken = url.searchParams.get("transferToken");
    }
    let transferData = null;
    if (transferToken) {
      const transferKey = this.transferKey(transferToken);
      transferData = await this.room.storage.get(transferKey);
      if (transferData) {
        if (this.isTransferExpired(transferData, transferExpiryTime)) {
          transferData = null;
        }
        await this.room.storage.delete(transferKey);
      }
    }
    const requestedPrivateId = this.getPrivateId(conn);
    const privateId = transferData?.privateId || requestedPrivateId;
    const existingSession = await this.getSession(privateId);
    const publicId = existingSession?.publicId || transferData?.publicId || generateShortUUID2();
    let user = null;
    const signal2 = this.getUsersProperty(subRoom);
    const usersPropName = this.getUsersPropName(subRoom);
    const shouldPersistState = subRoom["persistState"] !== false;
    if (signal2) {
      const { classType } = signal2.options;
      const createUser = async (id2, snapshot) => {
        const nextUser = this.createUserFromClassType(classType, conn, ctx);
        signal2()[id2] = nextUser;
        if (snapshot !== void 0) {
          load(nextUser, snapshot, true);
        }
        if (shouldPersistState) {
          const persistedSnapshot = createStatesSnapshotDeep(nextUser);
          await this.saveStatePath(`${usersPropName}.${id2}`, persistedSnapshot);
        }
        return nextUser;
      };
      if (!existingSession?.publicId) {
        if (transferData?.restored && signal2()[publicId]) {
          user = signal2()[publicId];
        } else if (transferData?.restored && transferData.userSnapshot !== void 0) {
          user = await createUser(publicId, transferData.userSnapshot);
        } else {
          user = await createUser(publicId);
        }
      } else {
        user = signal2()[existingSession.publicId];
        if (!user) {
          const snapshot = transferData?.publicId === existingSession.publicId ? transferData.userSnapshot : void 0;
          user = await createUser(existingSession.publicId, snapshot);
        }
      }
      if (!existingSession) {
        await this.saveSession(privateId, {
          publicId
        });
      } else {
        await this.updateSessionConnection(privateId, true);
      }
    }
    this.updateUserConnectionStatus(user, true);
    conn.setState({
      ...conn.state,
      publicId,
      privateId
    });
    await awaitReturn(subRoom["onJoin"]?.(user, conn, ctx));
    if (subRoom.$autoSync) {
      this.send(conn, {
        type: "sync",
        value: {
          pId: publicId,
          ...subRoom.$memoryAll
        }
      }, subRoom);
    } else {
      subRoom.$pendingInitialSync.set(conn, publicId);
    }
  }
  /**
   * @method onConnect
   * @async
   * @param {Party.Connection} conn - The connection object for the new user.
   * @param {Party.ConnectionContext} ctx - The context of the connection.
   * @description Handles a new user connection, creates a user object, and sends initial sync data.
   * @returns {Promise<void>}
   * 
   * @example
   * ```typescript
   * server.onConnect = async (conn, ctx) => {
   *   await server.onConnect(conn, ctx);
   *   console.log("New user connected:", conn.id);
   * };
   * ```
   */
  async onConnect(conn, ctx) {
    if (ctx.request?.headers.has("x-shard-id")) {
      if (!this.isAuthorizedShardRequest(ctx.request)) {
        conn.close();
        return;
      }
      this.onConnectShard(conn, ctx);
    } else {
      await this.onConnectClient(conn, ctx);
    }
  }
  isAuthorizedShardRequest(req) {
    const shardSecret = this.room.env.SHARD_SECRET;
    return typeof shardSecret === "string" && shardSecret.length > 0 && req?.headers.get("x-access-shard") === shardSecret;
  }
  /**
   * @method onConnectShard
   * @private
   * @param {Party.Connection} conn - The connection object for the new shard.
   * @param {Party.ConnectionContext} ctx - The context of the shard connection.
   * @description Handles a new shard connection, setting up the necessary state.
   * @returns {void}
   */
  onConnectShard(conn, ctx) {
    const shardId = ctx.request?.headers.get("x-shard-id") || "unknown-shard";
    const worldId = ctx.request?.headers.get("x-shard-world-id") || "world-default";
    conn.setState({
      shard: true,
      shardId,
      worldId,
      clients: /* @__PURE__ */ new Map()
      // Track clients connected through this shard
    });
  }
  /**
   * @method onMessage
   * @async
   * @param {string} message - The message received from a user or shard.
   * @param {Party.Connection} sender - The connection object of the sender.
   * @description Processes incoming messages, handling differently based on if sender is shard or client.
   * @returns {Promise<void>}
   */
  async onMessage(message, sender) {
    if (sender.state && sender.state.shard) {
      await this.handleShardMessage(message, sender);
      return;
    }
    let json;
    try {
      json = JSON.parse(message);
    } catch (e) {
      return;
    }
    const result = Message.safeParse(json);
    if (!result.success) {
      return;
    }
    const subRoom = await this.getSubRoom();
    if (!subRoom) {
      console.warn("Room not found");
      return;
    }
    const roomGuards = subRoom.constructor["_roomGuards"] || [];
    for (const guard of roomGuards) {
      const isAuthorized = await guard(sender, result.data.value, this.room);
      if (!isAuthorized) {
        return;
      }
    }
    const actions = subRoom.constructor["_actionMetadata"];
    const signal2 = this.getUsersProperty(subRoom);
    const { publicId } = sender.state;
    const user = signal2?.()[publicId];
    const actionName = actions?.get(result.data.action);
    if (actionName) {
      const guards = subRoom.constructor["_actionGuards"]?.get(actionName.key) || [];
      for (const guard of guards) {
        const isAuthorized = await guard(sender, result.data.value, this.room);
        if (!isAuthorized) {
          return;
        }
      }
      if (actionName.bodyValidation) {
        const bodyResult = actionName.bodyValidation.safeParse(
          result.data.value
        );
        if (!bodyResult.success) {
          return;
        }
      }
      await awaitReturn(
        subRoom[actionName.key](user, result.data.value, sender)
      );
      return;
    }
    const unhandledAction = subRoom.constructor["_unhandledActionMetadata"];
    if (unhandledAction) {
      const guards = subRoom.constructor["_actionGuards"]?.get(unhandledAction.key) || [];
      for (const guard of guards) {
        const isAuthorized = await guard(sender, result.data, this.room);
        if (!isAuthorized) {
          return;
        }
      }
      await awaitReturn(
        subRoom[unhandledAction.key](user, result.data, sender)
      );
    }
  }
  /**
   * @method handleShardMessage
   * @private
   * @async
   * @param {string} message - The message received from a shard.
   * @param {Party.Connection} shardConnection - The connection object of the shard.
   * @description Processes messages from shards, extracting client information.
   * @returns {Promise<void>}
   */
  async handleShardMessage(message, shardConnection) {
    let parsedMessage;
    try {
      parsedMessage = JSON.parse(message);
    } catch (e) {
      console.error("Error parsing shard message:", e);
      return;
    }
    const shardState = shardConnection.state;
    const clients = shardState.clients;
    switch (parsedMessage.type) {
      case "shard.clientConnected":
        await this.handleShardClientConnect(parsedMessage, shardConnection);
        break;
      case "shard.clientMessage":
        await this.handleShardClientMessage(parsedMessage, shardConnection);
        break;
      case "shard.clientDisconnected":
        await this.handleShardClientDisconnect(parsedMessage, shardConnection);
        break;
      default:
        console.warn(`Unknown shard message type: ${parsedMessage.type}`);
    }
  }
  /**
   * @method handleShardClientConnect
   * @private
   * @async
   * @param {Object} message - The client connection message from a shard.
   * @param {Party.Connection} shardConnection - The connection object of the shard.
   * @description Handles a new client connection via a shard.
   * @returns {Promise<void>}
   */
  async handleShardClientConnect(message, shardConnection) {
    const { privateId, requestInfo } = message;
    const shardState = shardConnection.state;
    const virtualContext = {
      request: requestInfo ? {
        headers: new Headers(requestInfo.headers),
        method: requestInfo.method,
        url: requestInfo.url
      } : void 0
    };
    const virtualConnection = {
      id: privateId,
      send: (data) => {
        shardConnection.send(JSON.stringify({
          targetClientId: privateId,
          data
        }));
      },
      state: {},
      setState: (state) => {
        const clients = shardState.clients;
        const currentState = clients.get(privateId) || {};
        const mergedState = Object.assign({}, currentState, state);
        clients.set(privateId, mergedState);
        virtualConnection.state = clients.get(privateId);
        return virtualConnection.state;
      },
      close: () => {
        shardConnection.send(JSON.stringify({
          type: "shard.closeClient",
          privateId
        }));
        if (shardState.clients) {
          shardState.clients.delete(privateId);
        }
      }
    };
    if (!shardState.clients.has(privateId)) {
      shardState.clients.set(privateId, {});
    }
    await this.onConnectClient(virtualConnection, virtualContext);
  }
  /**
   * @method handleShardClientMessage
   * @private
   * @async
   * @param {Object} message - The client message from a shard.
   * @param {Party.Connection} shardConnection - The connection object of the shard.
   * @description Handles a message from a client via a shard.
   * @returns {Promise<void>}
   */
  async handleShardClientMessage(message, shardConnection) {
    const { privateId, publicId, payload } = message;
    const shardState = shardConnection.state;
    const clients = shardState.clients;
    if (!clients.has(privateId)) {
      console.warn(`Received message from unknown client ${privateId}, creating virtual connection`);
      clients.set(privateId, { publicId });
    }
    const virtualConnection = {
      id: privateId,
      send: (data) => {
        shardConnection.send(JSON.stringify({
          targetClientId: privateId,
          data
        }));
      },
      state: clients.get(privateId),
      setState: (state) => {
        const currentState = clients.get(privateId) || {};
        const mergedState = Object.assign({}, currentState, state);
        clients.set(privateId, mergedState);
        virtualConnection.state = clients.get(privateId);
        return virtualConnection.state;
      },
      close: () => {
        shardConnection.send(JSON.stringify({
          type: "shard.closeClient",
          privateId
        }));
        if (shardState.clients) {
          shardState.clients.delete(privateId);
        }
      }
    };
    const payloadString = typeof payload === "string" ? payload : JSON.stringify(payload);
    await this.onMessage(payloadString, virtualConnection);
  }
  /**
   * @method handleShardClientDisconnect
   * @private
   * @async
   * @param {Object} message - The client disconnection message from a shard.
   * @param {Party.Connection} shardConnection - The connection object of the shard.
   * @description Handles a client disconnection via a shard.
   * @returns {Promise<void>}
   */
  async handleShardClientDisconnect(message, shardConnection) {
    const { privateId, publicId } = message;
    const shardState = shardConnection.state;
    const clients = shardState.clients;
    const clientState = clients.get(privateId);
    if (!clientState) {
      console.warn(`Disconnection for unknown client ${privateId}`);
      return;
    }
    const virtualConnection = {
      id: privateId,
      send: () => {
      },
      // No-op since client is disconnecting
      state: clientState,
      setState: () => {
        return {};
      },
      close: () => {
      }
    };
    await this.onClose(virtualConnection);
    clients.delete(privateId);
  }
  /**
   * @method onClose
   * @async
   * @param {Party.Connection} conn - The connection object of the disconnecting user.
   * @description Handles user disconnection, removing them from the room and triggering the onLeave event..
   * @returns {Promise<void>}
   * 
   * @example
   * ```typescript
   * server.onClose = async (conn) => {
   *   await server.onClose(conn);
   *   console.log("User disconnected:", conn.id);
   * };
   * ```
   */
  async onClose(conn) {
    const subRoom = await this.getSubRoom();
    if (!subRoom) {
      return;
    }
    if (subRoom.$pendingInitialSync) {
      subRoom.$pendingInitialSync.delete(conn);
    }
    const signal2 = this.getUsersProperty(subRoom);
    if (!conn.state) {
      return;
    }
    const privateId = this.getPrivateId(conn);
    const { publicId } = conn.state;
    const user = signal2?.()[publicId];
    if (!user) return;
    if (this.hasActiveSessionConnection(privateId)) {
      return;
    }
    await this.updateSessionConnection(privateId, false);
    this.scheduleSessionGarbageCollector(this.getSessionExpiryTime(subRoom), privateId);
    const connectionUpdated = this.updateUserConnectionStatus(user, false);
    await awaitReturn(subRoom["onLeave"]?.(user, conn));
    if (!connectionUpdated) {
      this.broadcast({
        type: "user_disconnected",
        value: { publicId }
      }, subRoom);
    }
  }
  async onAlarm() {
    const subRoom = await this.getSubRoom();
    await awaitReturn(subRoom["onAlarm"]?.(subRoom));
  }
  async onError(connection, error) {
    const subRoom = await this.getSubRoom();
    await awaitReturn(subRoom["onError"]?.(connection, error));
  }
  /**
   * @method onRequest
   * @async
   * @param {Party.Request} req - The HTTP request to handle
   * @description Handles HTTP requests, either directly from clients or forwarded by shards
   * @returns {Promise<Response>} The response to return to the client
   */
  async onRequest(req) {
    const isFromShard = req.headers.has("x-forwarded-by-shard");
    const shardId = req.headers.get("x-shard-id");
    const res = new ServerResponse([
      createCorsInterceptor()
    ]);
    if (req.method === "OPTIONS") {
      return res.status(200).send({});
    }
    if (isFromShard) {
      if (!this.isAuthorizedShardRequest(req)) {
        return res.unauthorized("Invalid shard credentials");
      }
      return this.handleShardRequest(req, res, shardId);
    }
    return this.handleDirectRequest(req, res);
  }
  /**
   * @method handleSessionRestore
   * @private
   * @async
   * @param {Party.Request} req - The HTTP request for session restore
   * @param {ServerResponse} res - The response object
   * @description Handles session restoration from transfer data, creates session from privateId
   * @returns {Promise<Response>} The response to return to the client
   */
  async handleSessionRestore(req, res) {
    try {
      const transferData = await req.json();
      const { privateId, userSnapshot, sessionState, publicId } = transferData;
      if (!privateId || !publicId) {
        return res.badRequest("Missing privateId or publicId in transfer data");
      }
      const subRoom = await this.getSubRoom();
      if (!subRoom) {
        return res.serverError("Room not available");
      }
      const shouldPersistState = subRoom["persistState"] !== false;
      let restoredUserSnapshot;
      await this.saveSession(privateId, {
        publicId,
        state: sessionState,
        created: Date.now(),
        connected: false
        // Will be set to true when user connects
      });
      if (userSnapshot) {
        const signal2 = this.getUsersProperty(subRoom);
        const usersPropName = this.getUsersPropName(subRoom);
        if (signal2 && usersPropName) {
          const { classType } = signal2.options;
          const user = this.createUserFromClassType(classType);
          const hydratedSnapshot = await awaitReturn(
            subRoom["onSessionRestore"]?.({
              userSnapshot,
              user,
              publicId,
              privateId,
              sessionState,
              room: this.room
            })
          ) ?? userSnapshot;
          signal2()[publicId] = user;
          load(user, hydratedSnapshot, true);
          restoredUserSnapshot = createStatesSnapshotDeep(user);
          if (shouldPersistState) {
            await this.saveStatePath(`${usersPropName}.${publicId}`, restoredUserSnapshot);
          }
        }
      }
      const transferToken = generateShortUUID2();
      await this.room.storage.put(this.transferKey(transferToken), {
        privateId,
        publicId,
        restored: true,
        ...shouldPersistState || restoredUserSnapshot === void 0 ? {} : { userSnapshot: restoredUserSnapshot },
        created: Date.now()
      });
      return res.success({ transferToken });
    } catch (error) {
      console.error("Error restoring session:", error);
      return res.serverError("Failed to restore session");
    }
  }
  /**
   * @method handleDirectRequest
   * @private
   * @async
   * @param {Party.Request} req - The HTTP request received directly from a client
   * @description Processes requests received directly from clients
   * @returns {Promise<Response>} The response to return to the client
   */
  async handleDirectRequest(req, res) {
    const subRoom = await this.getSubRoom();
    if (!subRoom) {
      return res.notFound();
    }
    const url = new URL(req.url);
    if (url.pathname.endsWith("/session-transfer") && req.method === "POST") {
      return this.handleSessionRestore(req, res);
    }
    const response2 = await this.tryMatchRequestHandler(req, res, subRoom);
    if (response2) {
      return response2;
    }
    const legacyResponse = await awaitReturn(subRoom["onRequest"]?.(req, res));
    if (!legacyResponse) {
      return res.notFound();
    }
    if (legacyResponse instanceof Response) {
      return legacyResponse;
    }
    return res.success(legacyResponse);
  }
  /**
   * @method tryMatchRequestHandler
   * @private
   * @async
   * @param {Party.Request} req - The HTTP request to handle
   * @param {Object} subRoom - The room instance
   * @description Attempts to match the request to a registered @Request handler
   * @returns {Promise<Response | null>} The response or null if no handler matched
   */
  async tryMatchRequestHandler(req, res, subRoom) {
    const requestHandlers = subRoom.constructor["_requestMetadata"];
    if (!requestHandlers) {
      return null;
    }
    const url = new URL(req.url);
    const method = req.method;
    let pathname = url.pathname;
    pathname = "/" + pathname.split("/").slice(4).join("/");
    for (const [routeKey, handler] of requestHandlers.entries()) {
      const firstColonIndex = routeKey.indexOf(":");
      const handlerMethod = routeKey.substring(0, firstColonIndex);
      const handlerPath = routeKey.substring(firstColonIndex + 1);
      if (handlerMethod !== method) {
        continue;
      }
      if (this.pathMatches(pathname, handlerPath)) {
        const params = this.extractPathParams(pathname, handlerPath);
        const guards = subRoom.constructor["_actionGuards"]?.get(handler.key) || [];
        for (const guard of guards) {
          const isAuthorized = await guard(null, req, this.room);
          if (isAuthorized instanceof Response) {
            return isAuthorized;
          }
          if (!isAuthorized) {
            return res.notPermitted();
          }
        }
        let bodyData = null;
        if (handler.bodyValidation && ["POST", "PUT", "PATCH"].includes(method)) {
          try {
            const contentType = req.headers.get("content-type") || "";
            if (contentType.includes("application/json")) {
              const body = await req.json();
              const validation = handler.bodyValidation.safeParse(body);
              if (!validation.success) {
                return res.badRequest("Invalid request body", {
                  details: validation.error
                });
              }
              bodyData = validation.data;
            }
          } catch (error) {
            return res.badRequest("Failed to parse request body");
          }
        }
        try {
          req["data"] = bodyData;
          req["params"] = params;
          const result = await awaitReturn(
            subRoom[handler.key](req, res)
          );
          if (result instanceof Response) {
            return result;
          }
          return res.success(result);
        } catch (error) {
          console.error("Error executing request handler:", error);
          return res.serverError();
        }
      }
    }
    return null;
  }
  /**
   * @method pathMatches
   * @private
   * @param {string} requestPath - The path from the request
   * @param {string} handlerPath - The path pattern from the handler
   * @description Checks if a request path matches a handler path pattern
   * @returns {boolean} True if the paths match
   */
  pathMatches(requestPath, handlerPath) {
    const pathRegexString = handlerPath.replace(/\//g, "\\/").replace(/:([^\/]+)/g, "([^/]+)");
    const pathRegex = new RegExp(`^${pathRegexString}`);
    return pathRegex.test(requestPath);
  }
  /**
   * @method extractPathParams
   * @private
   * @param {string} requestPath - The path from the request
   * @param {string} handlerPath - The path pattern from the handler
   * @description Extracts path parameters from the request path based on the handler pattern
   * @returns {Object} An object containing the path parameters
   */
  extractPathParams(requestPath, handlerPath) {
    const params = {};
    const paramNames = [];
    handlerPath.split("/").forEach((segment) => {
      if (segment.startsWith(":")) {
        paramNames.push(segment.substring(1));
      }
    });
    const pathRegexString = handlerPath.replace(/\//g, "\\/").replace(/:([^\/]+)/g, "([^/]+)");
    const pathRegex = new RegExp(`^${pathRegexString}`);
    const matches = requestPath.match(pathRegex);
    if (matches && matches.length > 1) {
      for (let i = 0; i < paramNames.length; i++) {
        params[paramNames[i]] = matches[i + 1];
      }
    }
    return params;
  }
  /**
   * @method handleShardRequest
   * @private
   * @async
   * @param {Party.Request} req - The HTTP request forwarded by a shard
   * @param {string | null} shardId - The ID of the shard that forwarded the request
   * @description Processes requests forwarded by shards, preserving client context
   * @returns {Promise<Response>} The response to return to the shard (which will forward it to the client)
   */
  async handleShardRequest(req, res, shardId) {
    const subRoom = await this.getSubRoom();
    if (!subRoom) {
      return res.notFound();
    }
    const originalClientIp = req.headers.get("x-original-client-ip");
    const enhancedReq = this.createEnhancedRequest(req, originalClientIp);
    try {
      const response2 = await this.tryMatchRequestHandler(enhancedReq, res, subRoom);
      if (response2) {
        return response2;
      }
      const legacyResponse = await awaitReturn(subRoom["onRequest"]?.(enhancedReq, res));
      if (!legacyResponse) {
        return res.notFound();
      }
      if (legacyResponse instanceof Response) {
        return legacyResponse;
      }
      return res.success(legacyResponse);
    } catch (error) {
      console.error(`Error processing request from shard ${shardId}:`, error);
      return res.serverError();
    }
  }
  /**
   * @method createEnhancedRequest
   * @private
   * @param {Party.Request} originalReq - The original request received from the shard
   * @param {string | null} originalClientIp - The original client IP, if available
   * @description Creates an enhanced request object that preserves the original client context
   * @returns {Party.Request} The enhanced request object
   */
  createEnhancedRequest(originalReq, originalClientIp) {
    const clonedReq = originalReq.clone();
    clonedReq.viaShard = true;
    if (originalClientIp) {
      clonedReq.originalClientIp = originalClientIp;
    }
    return clonedReq;
  }
};

// src/shard.ts
var Shard = class {
  constructor(room, options = {}) {
    this.room = room;
    this.connectionMap = /* @__PURE__ */ new Map();
    this.worldUrl = null;
    this.lastReportedConnections = 0;
    this.statsInterval = 3e4;
    this.statsIntervalId = null;
    this.worldUrl = options.worldUrl ?? null;
    this.worldId = options.worldId ?? this.getWorldIdFromShardId(room.id) ?? this.getEnvString("WORLD_ID") ?? this.getEnvString("SIGNE_WORLD_ID") ?? "world-default";
    this.statsInterval = options.statsInterval ?? this.statsInterval;
  }
  getPrivateId(conn) {
    return conn.sessionId || conn.id;
  }
  getEnvString(key) {
    const value = this.room.env?.[key];
    return typeof value === "string" && value.length > 0 ? value : void 0;
  }
  getRoomIdFromShardId(shardId) {
    return shardId.split(":")[0];
  }
  getWorldIdFromShardId(shardId) {
    const parts = shardId.split(":");
    return parts.length >= 3 ? parts[1] : void 0;
  }
  async onStart() {
    const roomId = this.getRoomIdFromShardId(this.room.id);
    const roomStub = this.room.context.parties.main.get(roomId);
    if (!roomStub) {
      console.warn("No room room stub found in main party context");
      return;
    }
    this.mainServerStub = roomStub;
    this.ws = await roomStub.socket({
      headers: {
        "x-shard-id": this.room.id,
        "x-shard-world-id": this.worldId,
        "x-access-shard": this.room.env.SHARD_SECRET
      }
    });
    this.ws.addEventListener("message", (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === "shard.closeClient" && message.privateId) {
          const clientConnections = this.connectionMap.get(message.privateId);
          if (clientConnections?.size) {
            for (const clientConn of [...clientConnections]) {
              clientConn.close();
            }
          }
          return;
        }
        if (message.targetClientId) {
          const clientConnections = this.connectionMap.get(message.targetClientId);
          if (clientConnections?.size) {
            delete message.targetClientId;
            for (const clientConn of clientConnections) {
              clientConn.send(message.data);
            }
          }
        } else {
          this.room.broadcast(event.data);
        }
      } catch (error) {
        console.error("Error processing message from main server:", error);
      }
    });
    await this.updateWorldStats(true);
    this.startPeriodicStatsUpdates();
  }
  startPeriodicStatsUpdates() {
    if (this.statsInterval <= 0 || !this.room.context.parties.world) {
      return;
    }
    if (this.statsIntervalId) {
      clearInterval(this.statsIntervalId);
    }
    this.statsIntervalId = setInterval(() => {
      this.updateWorldStats(true).catch((error) => {
        console.error("Error in periodic stats update:", error);
      });
    }, this.statsInterval);
    this.statsIntervalId?.unref?.();
  }
  stopPeriodicStatsUpdates() {
    if (this.statsIntervalId) {
      clearInterval(this.statsIntervalId);
      this.statsIntervalId = null;
    }
  }
  onConnect(conn, ctx) {
    const privateId = this.getPrivateId(conn);
    const connections = this.connectionMap.get(privateId) ?? /* @__PURE__ */ new Set();
    connections.add(conn);
    this.connectionMap.set(privateId, connections);
    const headers = {};
    if (ctx.request?.headers) {
      ctx.request.headers.forEach((value, key) => {
        headers[key] = value;
      });
    }
    const requestInfo = ctx.request ? {
      headers,
      url: ctx.request.url,
      method: ctx.request.method
    } : null;
    this.ws.send(JSON.stringify({
      type: "shard.clientConnected",
      privateId,
      requestInfo
    }));
    this.updateWorldStats();
  }
  onMessage(message, sender) {
    try {
      const parsedMessage = typeof message === "string" ? JSON.parse(message) : message;
      const wrappedMessage = JSON.stringify({
        type: "shard.clientMessage",
        privateId: this.getPrivateId(sender),
        publicId: sender.state?.publicId,
        payload: parsedMessage
      });
      this.ws.send(wrappedMessage);
    } catch (error) {
      console.error("Error forwarding message to main server:", error);
    }
  }
  onClose(conn) {
    const privateId = this.getPrivateId(conn);
    const connections = this.connectionMap.get(privateId);
    connections?.delete(conn);
    if (connections?.size) {
      this.updateWorldStats();
      return;
    }
    this.connectionMap.delete(privateId);
    this.ws.send(JSON.stringify({
      type: "shard.clientDisconnected",
      privateId,
      publicId: conn.state?.publicId
    }));
    this.updateWorldStats();
  }
  async updateWorldStats(force = false) {
    const currentConnections = Array.from(this.connectionMap.values()).reduce((total, connections) => total + connections.size, 0);
    if (!force && currentConnections === this.lastReportedConnections) {
      return true;
    }
    try {
      const worldParty = this.room.context.parties.world;
      if (!worldParty) {
        return false;
      }
      const worldRoom = worldParty.get(this.worldId);
      if (!worldRoom?.fetch) {
        return false;
      }
      const response2 = await worldRoom.fetch("/update-shard", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-shard": this.room.env.SHARD_SECRET
        },
        body: JSON.stringify({
          shardId: this.room.id,
          worldId: this.worldId,
          connections: currentConnections
        })
      });
      if (!response2.ok) {
        const errorData = await response2.json().catch(() => ({ error: "Unknown error" }));
        console.error(`Failed to update World stats: ${response2.status} - ${errorData.error || "Unknown error"}`);
        return false;
      }
      this.lastReportedConnections = currentConnections;
      return true;
    } catch (error) {
      console.error("Error updating World stats:", error);
      return false;
    }
  }
  /**
   * @method onRequest
   * @async
   * @param {Party.Request} req - The HTTP request to handle
   * @description Forwards HTTP requests to the main server, preserving client context
   * @returns {Promise<Response>} The response from the main server
   */
  async onRequest(req) {
    if (!this.mainServerStub) {
      return response(503, { error: "Shard not connected to main server" });
    }
    try {
      const url = new URL(req.url);
      const path = url.pathname;
      const method = req.method;
      let body = null;
      if (method !== "GET" && method !== "HEAD") {
        body = await req.text();
      }
      const headers = new Headers();
      req.headers.forEach((value, key) => {
        headers.set(key, value);
      });
      headers.set("x-shard-id", this.room.id);
      headers.set("x-shard-world-id", this.worldId);
      headers.set("x-forwarded-by-shard", "true");
      headers.set("x-access-shard", this.room.env.SHARD_SECRET);
      const clientIp = req.headers.get("x-forwarded-for") || "unknown";
      if (clientIp) {
        headers.set("x-original-client-ip", clientIp);
      }
      const requestInit = {
        method,
        headers,
        body
      };
      const response2 = await this.mainServerStub.fetch(path + url.search, requestInit);
      return response2;
    } catch (error) {
      return response(500, { error: "Error forwarding request" });
    }
  }
  /**
   * @method onAlarm
   * @async
   * @description Executed periodically, used to perform maintenance tasks
   */
  async onAlarm() {
    await this.updateWorldStats(true);
  }
};

// src/testing.ts
async function testRoom(Room2, options = {}) {
  const createServer = (io2) => {
    const server2 = new Server(io2);
    server2.rooms = [Room2];
    return server2;
  };
  const isShard = options.shard || false;
  const io = new ServerIo(options.id ?? Room2.path, isShard ? {
    parties: {
      game: createServer,
      ...options.parties || {}
    },
    partyFn: options.partyFn,
    env: options.env
  } : {
    parties: options.parties,
    partyFn: options.partyFn,
    env: options.env
  });
  Room2.prototype.throttleSync = 0;
  Room2.prototype.throttleStorage = 0;
  Room2.prototype.options = options;
  let server;
  if (options.shard) {
    const shardServer = new Shard(io);
    shardServer.subRoom = null;
    server = shardServer;
    if (io.context.parties.main instanceof Map) {
      for (const lobby of io.context.parties.main.values()) {
        await lobby.server.onStart();
      }
    }
  } else {
    server = await createServer(io);
    if (io.context.parties.main instanceof Map) {
      for (const lobby of io.context.parties.main.values()) {
        if (lobby.server && lobby.server !== server) {
          await lobby.server.onStart();
        }
      }
    }
  }
  await server.onStart();
  return {
    server,
    room: server.subRoom,
    createClient: async (id2, opts) => {
      const client = await io.connection(server, id2, opts);
      return client;
    },
    getServerUser: async (client, prop = "users") => {
      const privateId = client.conn.sessionId || client.conn.id;
      const session = await server.getSession(privateId);
      return server.subRoom[prop]()[session?.publicId];
    }
  };
}
async function request(room, path, options = {
  method: "GET"
}) {
  const url = new URL("http://localhost" + path);
  const request2 = new Request(url.toString(), options);
  const response2 = await room.onRequest(request2);
  return response2;
}
function tick(ms = 0) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// src/mock.ts
var MockPartyClient = class {
  constructor(server, sessionId) {
    this.server = server;
    this.events = /* @__PURE__ */ new Map();
    this.id = generateShortUUID();
    this.conn = new MockConnection(this, sessionId || this.id);
  }
  addEventListener(event, cb) {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event).push(cb);
  }
  removeEventListener(event, cb) {
    if (!this.events.has(event)) return;
    const callbacks = this.events.get(event);
    const index = callbacks.indexOf(cb);
    if (index !== -1) {
      callbacks.splice(index, 1);
    }
    if (callbacks.length === 0) {
      this.events.delete(event);
    }
  }
  _trigger(event, data) {
    const callbacks = this.events.get(event);
    if (callbacks) {
      for (const cb of callbacks) {
        cb(data);
      }
    }
  }
  send(data) {
    return this.server.onMessage(JSON.stringify(data), this.conn);
  }
};
var MockLobby = class {
  constructor(server, lobbyId) {
    this.server = server;
    this.lobbyId = lobbyId;
  }
  socket(init) {
    return new MockPartyClient(this.server, init?.id);
  }
  async connection(idOrOptions, maybeOptions) {
    const id2 = typeof idOrOptions === "string" ? idOrOptions : idOrOptions?.id;
    const options = (typeof idOrOptions === "string" ? maybeOptions : idOrOptions) || {};
    return this.server.room.connection(this.server, id2, options);
  }
  fetch(url, options) {
    const baseUrl = url.includes("shard") ? "" : "/parties/main/" + this.lobbyId;
    return request(this.server, baseUrl + url, options);
  }
};
var MockContext = class {
  constructor(room, options = {}) {
    this.room = room;
    this.parties = {
      main: /* @__PURE__ */ new Map()
    };
    const parties = options.parties || {};
    if (options.partyFn) {
      const serverCache = /* @__PURE__ */ new Map();
      this.parties.main = {
        get: async (lobbyId) => {
          if (!serverCache.has(lobbyId)) {
            const server = await options.partyFn(lobbyId);
            serverCache.set(lobbyId, new MockLobby(server, lobbyId));
          }
          return serverCache.get(lobbyId);
        }
      };
    } else {
      for (let lobbyId in parties) {
        const server = parties[lobbyId](room);
        this.parties.main.set(lobbyId, new MockLobby(server, lobbyId));
      }
    }
  }
};
var MockPartyRoom = class {
  constructor(id2, options = {}) {
    this.id = id2;
    this.clients = /* @__PURE__ */ new Map();
    this.storage = new Storage();
    this.env = {};
    this.id = id2 || generateShortUUID();
    this.context = new MockContext(this, {
      parties: options.parties,
      partyFn: options.partyFn
    });
    this.env = options.env || {};
  }
  async connection(server, id2, opts) {
    const socket = new MockPartyClient(server, id2);
    const url = new URL("http://localhost");
    if (opts?.query) {
      for (const [key, value] of Object.entries(opts.query)) {
        url.searchParams.set(key, String(value));
      }
    }
    const request2 = new Request(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...opts?.headers || {}
      }
    });
    await server.onConnect(socket.conn, { request: request2 });
    this.clients.set(socket.id, socket);
    return socket;
  }
  broadcast(data) {
    this.clients.forEach((client) => {
      client._trigger("message", data);
    });
  }
  getConnection(id2) {
    let connection;
    for (const client of this.clients.values()) {
      if (client.conn.id === id2 || client.conn.sessionId === id2) {
        connection = client.conn;
      }
    }
    return connection;
  }
  getConnections() {
    return Array.from(this.clients.values()).map((client) => client.conn);
  }
  clear() {
    this.clients.clear();
  }
  deleteConnection(id2, connection) {
    if (connection) {
      this.clients.delete(connection.id);
      return;
    }
    for (const [connectionKey, client] of this.clients) {
      if (client.conn.id === id2 || client.conn.sessionId === id2) {
        this.clients.delete(connectionKey);
      }
    }
  }
};
var MockConnection = class {
  constructor(client, sessionId) {
    this.client = client;
    this.state = {};
    this.server = client.server;
    this.id = client.id;
    this.sessionId = sessionId;
  }
  setState(value) {
    this.state = value;
  }
  send(data) {
    this.client._trigger("message", data);
  }
  close() {
    this.server.room.deleteConnection?.(this.id, this);
    this.server.onClose(this);
  }
};
var ServerIo = MockPartyRoom;
var ClientIo = MockPartyClient;

// src/world.ts
import { signal } from "@signe/reactive";
import { sync, id, persist } from "@signe/sync";
import { z as z2 } from "zod";

// src/jwt.ts
var JWTAuth = class {
  /**
   * Constructor for the JWTAuth class
   * @param {string} secret - The secret key used for signing and verifying tokens
   */
  constructor(secret) {
    if (!secret || typeof secret !== "string") {
      throw new Error("Secret is required and must be a string");
    }
    this.secret = secret;
    this.encoder = new TextEncoder();
    this.decoder = new TextDecoder();
  }
  /**
   * Convert the secret to a CryptoKey for HMAC operations
   * @returns {Promise<CryptoKey>} - The CryptoKey for HMAC operations
   */
  async getSecretKey() {
    const keyData = this.encoder.encode(this.secret);
    return await crypto.subtle.importKey(
      "raw",
      // format
      keyData,
      // key data
      {
        name: "HMAC",
        hash: { name: "SHA-256" }
      },
      false,
      // extractable
      ["sign", "verify"]
      // key usages
    );
  }
  /**
   * Base64Url encode a buffer
   * @param {ArrayBuffer} buffer - The buffer to encode
   * @returns {string} - The base64url encoded string
   */
  base64UrlEncode(buffer) {
    const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
    return base64.replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
  }
  /**
   * Base64Url decode a string
   * @param {string} base64Url - The base64url encoded string
   * @returns {ArrayBuffer} - The decoded buffer
   */
  base64UrlDecode(base64Url) {
    const padding = "=".repeat((4 - base64Url.length % 4) % 4);
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/") + padding;
    const rawData = atob(base64);
    const buffer = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) {
      buffer[i] = rawData.charCodeAt(i);
    }
    return buffer.buffer;
  }
  /**
   * Sign a payload and create a JWT token
   * @param {JWTPayload} payload - The payload to include in the token
   * @param {JWTOptions} [options={}] - Options for the token
   * @param {string | number} [options.expiresIn='1h'] - Token expiration time
   * @returns {Promise<string>} - The JWT token
   */
  async sign(payload, options = {}) {
    if (!payload || typeof payload !== "object") {
      throw new Error("Payload must be an object");
    }
    const expiresIn = options.expiresIn || "1h";
    let exp;
    if (typeof expiresIn === "number") {
      exp = Math.floor(Date.now() / 1e3) + expiresIn;
    } else if (typeof expiresIn === "string") {
      const match = expiresIn.match(/^(\d+)([smhd])$/);
      if (match) {
        const value = parseInt(match[1]);
        const unit = match[2];
        const seconds = {
          "s": value,
          "m": value * 60,
          "h": value * 60 * 60,
          "d": value * 60 * 60 * 24
        }[unit];
        exp = Math.floor(Date.now() / 1e3) + seconds;
      } else {
        throw new Error('Invalid expiresIn format. Use a number (seconds) or a string like "1h", "30m", etc.');
      }
    }
    const fullPayload = {
      ...payload,
      iat: Math.floor(Date.now() / 1e3),
      exp
    };
    const header = {
      alg: "HS256",
      typ: "JWT"
    };
    const encodedHeader = this.base64UrlEncode(this.encoder.encode(JSON.stringify(header)));
    const encodedPayload = this.base64UrlEncode(this.encoder.encode(JSON.stringify(fullPayload)));
    const signatureBase = `${encodedHeader}.${encodedPayload}`;
    const key = await this.getSecretKey();
    const signature = await crypto.subtle.sign(
      { name: "HMAC" },
      key,
      this.encoder.encode(signatureBase)
    );
    const encodedSignature = this.base64UrlEncode(signature);
    return `${signatureBase}.${encodedSignature}`;
  }
  /**
   * Verify a JWT token and return the decoded payload
   * @param {string} token - The JWT token to verify
   * @returns {Promise<JWTPayload>} - The decoded payload if verification succeeds
   * @throws {Error} - If verification fails
   */
  async verify(token) {
    if (!token || typeof token !== "string") {
      throw new Error("Token is required and must be a string");
    }
    const parts = token.split(".");
    if (parts.length !== 3) {
      throw new Error("Invalid token format");
    }
    const [encodedHeader, encodedPayload, encodedSignature] = parts;
    try {
      const header = JSON.parse(this.decoder.decode(this.base64UrlDecode(encodedHeader)));
      const payload = JSON.parse(this.decoder.decode(this.base64UrlDecode(encodedPayload)));
      if (header.alg !== "HS256") {
        throw new Error(`Unsupported algorithm: ${header.alg}`);
      }
      const now = Math.floor(Date.now() / 1e3);
      if (payload.exp && payload.exp < now) {
        throw new Error("Token has expired");
      }
      const key = await this.getSecretKey();
      const signatureBase = `${encodedHeader}.${encodedPayload}`;
      const signature = this.base64UrlDecode(encodedSignature);
      const isValid = await crypto.subtle.verify(
        { name: "HMAC" },
        key,
        signature,
        this.encoder.encode(signatureBase)
      );
      if (!isValid) {
        throw new Error("Invalid signature");
      }
      return payload;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Token verification failed: ${error.message}`);
      }
      throw new Error("Token verification failed: Unknown error");
    }
  }
};

// src/world.guard.ts
var guardManageWorld = async (_, req, room) => {
  const tokenShard = req.headers.get("x-access-shard");
  if (tokenShard) {
    if (tokenShard !== room.env.SHARD_SECRET) {
      return false;
    }
    return true;
  }
  const url = new URL(req.url);
  const token = getAuthToken(req, url);
  if (!token) {
    return false;
  }
  const jwt = new JWTAuth(room.env.AUTH_JWT_SECRET);
  try {
    const payload = await jwt.verify(token);
    if (!payload) {
      return false;
    }
    if (!canAccessWorld(payload, room.id)) {
      return false;
    }
  } catch (error) {
    return false;
  }
  return true;
};
function getAuthToken(req, url) {
  const authorization = req.headers.get("Authorization");
  if (authorization?.startsWith("Bearer ")) {
    return authorization.slice("Bearer ".length).trim();
  }
  return authorization ?? url.searchParams.get("world-auth-token");
}
function canAccessWorld(payload, worldId) {
  const worlds = payload.worlds;
  if (!Array.isArray(worlds)) {
    return false;
  }
  return worlds.some((world) => world === "*" || world === worldId);
}

// src/world.ts
var MAX_PLAYERS_PER_SHARD = 75;
var RoomConfigSchema = z2.object({
  name: z2.string(),
  balancingStrategy: z2.enum(["round-robin", "least-connections", "random"]),
  public: z2.boolean(),
  maxPlayersPerShard: z2.number().int().positive(),
  minShards: z2.number().int().min(0),
  maxShards: z2.number().int().positive().optional()
});
var RegisterShardSchema = z2.object({
  shardId: z2.string(),
  roomId: z2.string(),
  worldId: z2.string().optional(),
  url: z2.string().url(),
  maxConnections: z2.number().int().positive()
});
var UpdateShardStatsSchema = z2.object({
  shardId: z2.string(),
  worldId: z2.string().optional(),
  connections: z2.number().int().min(0),
  status: z2.enum(["active", "maintenance", "draining"]).optional()
});
var ScaleRoomSchema = z2.object({
  roomId: z2.string(),
  targetShardCount: z2.number().int().positive(),
  shardTemplate: z2.object({
    urlTemplate: z2.string(),
    maxConnections: z2.number().int().positive()
  }).optional()
});
var RoomConfig = class {
  constructor() {
    this.name = signal("");
    this.balancingStrategy = signal("round-robin");
    this.public = signal(true);
    this.maxPlayersPerShard = signal(MAX_PLAYERS_PER_SHARD);
    this.minShards = signal(1);
    this.maxShards = signal(void 0);
  }
};
__decorateClass([
  id()
], RoomConfig.prototype, "id", 2);
__decorateClass([
  sync()
], RoomConfig.prototype, "name", 2);
__decorateClass([
  sync()
], RoomConfig.prototype, "balancingStrategy", 2);
__decorateClass([
  sync()
], RoomConfig.prototype, "public", 2);
__decorateClass([
  sync()
], RoomConfig.prototype, "maxPlayersPerShard", 2);
__decorateClass([
  sync()
], RoomConfig.prototype, "minShards", 2);
__decorateClass([
  sync()
], RoomConfig.prototype, "maxShards", 2);
var ShardInfo = class {
  constructor() {
    this.roomId = signal("");
    this.worldId = signal("");
    this.url = signal("");
    this.currentConnections = signal(0);
    this.maxConnections = signal(MAX_PLAYERS_PER_SHARD);
    this.status = signal("active");
    this.lastHeartbeat = signal(0);
  }
};
__decorateClass([
  id()
], ShardInfo.prototype, "id", 2);
__decorateClass([
  sync()
], ShardInfo.prototype, "roomId", 2);
__decorateClass([
  sync()
], ShardInfo.prototype, "worldId", 2);
__decorateClass([
  sync()
], ShardInfo.prototype, "url", 2);
__decorateClass([
  sync({
    persist: false
  })
], ShardInfo.prototype, "currentConnections", 2);
__decorateClass([
  sync()
], ShardInfo.prototype, "maxConnections", 2);
__decorateClass([
  sync()
], ShardInfo.prototype, "status", 2);
__decorateClass([
  sync()
], ShardInfo.prototype, "lastHeartbeat", 2);
var WorldRoom = class {
  constructor(room) {
    this.room = room;
    this.rooms = signal({});
    this.shards = signal({});
    this.rrCounters = signal({});
    // Configuration
    this.defaultShardUrlTemplate = signal("{shardId}");
    this.defaultMaxConnectionsPerShard = signal(MAX_PLAYERS_PER_SHARD);
    const { AUTH_JWT_SECRET, SHARD_SECRET } = this.room.env;
    if (!AUTH_JWT_SECRET) {
      throw new Error("AUTH_JWT_SECRET env variable is not set");
    }
    if (!SHARD_SECRET) {
      throw new Error("SHARD_SECRET env variable is not set");
    }
    this.scheduleInactiveShardCleanup();
  }
  async onJoin(user, conn, ctx) {
    const canConnect = await guardManageWorld(user, ctx.request, this.room);
    conn.setState({
      ...conn.state,
      isAdmin: canConnect
    });
  }
  interceptorPacket(_, obj, conn) {
    if (!conn.state["isAdmin"]) {
      return null;
    }
    return obj;
  }
  // Helper methods
  getWorldId() {
    return this.room.id;
  }
  scheduleInactiveShardCleanup() {
    const timeoutId = setTimeout(() => this.cleanupInactiveShards(), 6e4);
    timeoutId?.unref?.();
  }
  cleanupInactiveShards() {
    const now = Date.now();
    const timeout = 5 * 60 * 1e3;
    const shardsValue = this.shards();
    let hasChanges = false;
    Object.values(shardsValue).forEach((shard) => {
      if (now - shard.lastHeartbeat() > timeout) {
        delete this.shards()[shard.id];
        hasChanges = true;
      }
    });
    this.scheduleInactiveShardCleanup();
  }
  removeShard(shardId) {
    delete this.shards()[shardId];
  }
  shouldCompleteDrain(shard) {
    return shard.status() === "draining" && shard.currentConnections() === 0;
  }
  async registerRoom(req, res) {
    const parseResult = RoomConfigSchema.safeParse(await req.json());
    if (!parseResult.success) {
      return res?.badRequest("Invalid room configuration", {
        details: parseResult.error
      });
    }
    const roomConfig = parseResult.data;
    if (roomConfig.maxShards !== void 0 && roomConfig.minShards > roomConfig.maxShards) {
      return res?.badRequest("minShards cannot be greater than maxShards");
    }
    const roomId = roomConfig.name;
    if (!this.rooms()[roomId]) {
      const newRoom = new RoomConfig();
      newRoom.id = roomId;
      newRoom.name.set(roomConfig.name);
      newRoom.balancingStrategy.set(roomConfig.balancingStrategy);
      newRoom.public.set(roomConfig.public);
      newRoom.maxPlayersPerShard.set(roomConfig.maxPlayersPerShard);
      newRoom.minShards.set(roomConfig.minShards);
      newRoom.maxShards.set(roomConfig.maxShards);
      this.rooms()[roomId] = newRoom;
      if (roomConfig.minShards > 0) {
        for (let i = 0; i < roomConfig.minShards; i++) {
          await this.createShard(roomId);
        }
      }
    } else {
      const room = this.rooms()[roomId];
      room.balancingStrategy.set(roomConfig.balancingStrategy);
      room.public.set(roomConfig.public);
      room.maxPlayersPerShard.set(roomConfig.maxPlayersPerShard);
      room.minShards.set(roomConfig.minShards);
      room.maxShards.set(roomConfig.maxShards);
    }
    await this.ensureMinShards(roomId);
  }
  async updateShardStats(req, res) {
    const parseResult = UpdateShardStatsSchema.safeParse(await req.json());
    if (!parseResult.success) {
      return res.badRequest("Invalid shard stats", {
        details: parseResult.error
      });
    }
    const body = parseResult.data;
    const { shardId, connections, status } = body;
    const shard = this.shards()[shardId];
    if (!shard) {
      return res.notFound(`Shard ${shardId} not found`);
    }
    if (body.worldId && body.worldId !== this.getWorldId()) {
      return res.badRequest(`Shard ${shardId} belongs to world ${body.worldId}, not ${this.getWorldId()}`);
    }
    shard.currentConnections.set(connections);
    if (status) {
      shard.status.set(status);
    }
    shard.lastHeartbeat.set(Date.now());
    if (this.shouldCompleteDrain(shard)) {
      this.removeShard(shard.id);
    }
  }
  async scaleRoom(req, res) {
    const parseResult = ScaleRoomSchema.safeParse(await req.json());
    if (!parseResult.success) {
      return res.badRequest("Invalid scale room request", {
        details: parseResult.error
      });
    }
    const data = parseResult.data;
    const { targetShardCount, shardTemplate, roomId } = data;
    const room = this.rooms()[roomId];
    if (!room) {
      return res.notFound(`Room ${roomId} does not exist`);
    }
    const roomShards = Object.values(this.shards()).filter((shard) => shard.roomId() === roomId);
    const previousShardCount = roomShards.length;
    if (room.maxShards() !== void 0 && targetShardCount > room.maxShards()) {
      return res.badRequest(`Cannot scale beyond maximum allowed shards (${room.maxShards()})`, {
        roomId,
        currentShardCount: previousShardCount
      });
    }
    if (targetShardCount < previousShardCount) {
      const shardsToDrain = [...roomShards].sort((a, b) => {
        if (a.status() === "draining" && b.status() !== "draining") return -1;
        if (a.status() !== "draining" && b.status() === "draining") return 1;
        return a.currentConnections() - b.currentConnections();
      }).slice(0, previousShardCount - targetShardCount);
      for (const shard of shardsToDrain) {
        shard.status.set("draining");
        if (this.shouldCompleteDrain(shard)) {
          this.removeShard(shard.id);
        }
      }
      return;
    }
    if (targetShardCount > previousShardCount) {
      const newShards = [];
      for (let i = 0; i < targetShardCount - previousShardCount; i++) {
        const newShard = await this.createShard(
          roomId,
          shardTemplate?.urlTemplate,
          shardTemplate?.maxConnections
        );
        if (newShard) {
          newShards.push(newShard);
        }
      }
    }
  }
  async connect(req, res) {
    try {
      let data;
      try {
        const body = await req.text();
        if (!body || body.trim() === "") {
          return res.badRequest("Request body is empty");
        }
        data = JSON.parse(body);
      } catch (parseError) {
        return res.badRequest("Invalid JSON in request body");
      }
      if (!data.roomId) {
        return res.badRequest("roomId parameter is required");
      }
      const autoCreate = data.autoCreate !== void 0 ? data.autoCreate : true;
      const result = await this.findOptimalShard(data.roomId, autoCreate);
      if ("error" in result) {
        return res.notFound(result.error);
      }
      return res.success({
        success: true,
        shardId: result.shardId,
        url: result.url
      });
    } catch (error) {
      console.error("Error connecting to shard:", error);
      return res.serverError();
    }
  }
  async findOptimalShard(roomId, autoCreate = true) {
    let room = this.rooms()[roomId];
    if (!room) {
      if (autoCreate) {
        const mockRequest = {
          json: async () => ({
            name: roomId,
            balancingStrategy: "round-robin",
            public: true,
            maxPlayersPerShard: this.defaultMaxConnectionsPerShard(),
            minShards: 1,
            maxShards: void 0
          })
        };
        await this.registerRoom(mockRequest);
        room = this.rooms()[roomId];
        if (!room) {
          return { error: `Failed to create room ${roomId}` };
        }
      } else {
        return { error: `Room ${roomId} does not exist` };
      }
    }
    const roomShards = Object.values(this.shards()).filter((shard) => shard.roomId() === roomId);
    if (roomShards.length === 0) {
      if (autoCreate) {
        const newShard = await this.createShard(roomId);
        if (newShard) {
          return {
            shardId: newShard.id,
            url: newShard.url()
          };
        } else {
          return { error: `Failed to create shard for room ${roomId}` };
        }
      } else {
        return { error: `No shards available for room ${roomId}` };
      }
    }
    let activeShards = this.getAvailableShards(roomShards);
    if (activeShards.length === 0) {
      if (autoCreate && this.canCreateShard(room, roomShards.length)) {
        const newShard = await this.createShard(roomId);
        if (newShard) {
          return {
            shardId: newShard.id,
            url: newShard.url()
          };
        }
      }
      if (roomShards.some((shard) => shard.status() === "active")) {
        return { error: `No shard capacity available for room ${roomId}` };
      }
      return { error: `No active shards available for room ${roomId}` };
    }
    const balancingStrategy = room.balancingStrategy();
    let selectedShard;
    switch (balancingStrategy) {
      case "least-connections":
        selectedShard = activeShards.reduce(
          (min, shard) => shard.currentConnections() < min.currentConnections() ? shard : min,
          activeShards[0]
        );
        break;
      case "random":
        selectedShard = activeShards[Math.floor(Math.random() * activeShards.length)];
        break;
      case "round-robin":
      default:
        const counter = this.rrCounters()[roomId] || 0;
        const nextCounter = (counter + 1) % activeShards.length;
        this.rrCounters()[roomId] = nextCounter;
        selectedShard = activeShards[counter];
        break;
    }
    return {
      shardId: selectedShard.id,
      url: selectedShard.url()
    };
  }
  getAvailableShards(shards) {
    return shards.filter(
      (shard) => shard && shard.status() === "active" && shard.currentConnections() < shard.maxConnections()
    );
  }
  canCreateShard(room, currentShardCount) {
    return room.maxShards() === void 0 || currentShardCount < room.maxShards();
  }
  // Private methods
  async createShard(roomId, urlTemplate, maxConnections) {
    const room = this.rooms()[roomId];
    if (!room) {
      console.error(`Cannot create shard for non-existent room: ${roomId}`);
      return null;
    }
    const worldId = this.getWorldId();
    const shardId = `${roomId}:${worldId}:${Date.now()}-${Math.floor(Math.random() * 1e4)}`;
    const template = urlTemplate || this.defaultShardUrlTemplate();
    const url = template.replace("{shardId}", shardId).replace("{roomId}", roomId);
    const max = maxConnections || room.maxPlayersPerShard();
    const newShard = new ShardInfo();
    newShard.id = shardId;
    newShard.roomId.set(roomId);
    newShard.worldId.set(worldId);
    newShard.url.set(url);
    newShard.maxConnections.set(max);
    newShard.currentConnections.set(0);
    newShard.status.set("active");
    newShard.lastHeartbeat.set(Date.now());
    this.shards()[shardId] = newShard;
    return newShard;
  }
  async ensureMinShards(roomId) {
    const room = this.rooms()[roomId];
    if (!room) {
      return;
    }
    const currentShardCount = Object.values(this.shards()).filter((shard) => shard.roomId() === roomId).length;
    const targetShardCount = room.minShards();
    if (currentShardCount >= targetShardCount) {
      return;
    }
    for (let i = currentShardCount; i < targetShardCount; i++) {
      await this.createShard(roomId);
    }
  }
};
__decorateClass([
  sync(RoomConfig)
], WorldRoom.prototype, "rooms", 2);
__decorateClass([
  sync(ShardInfo)
], WorldRoom.prototype, "shards", 2);
__decorateClass([
  persist()
], WorldRoom.prototype, "rrCounters", 2);
__decorateClass([
  Request2({
    path: "register-room",
    method: "POST"
  }),
  Guard([guardManageWorld])
], WorldRoom.prototype, "registerRoom", 1);
__decorateClass([
  Request2({
    path: "update-shard",
    method: "POST"
  }),
  Guard([guardManageWorld])
], WorldRoom.prototype, "updateShardStats", 1);
__decorateClass([
  Request2({
    path: "scale-room",
    method: "POST"
  }),
  Guard([guardManageWorld])
], WorldRoom.prototype, "scaleRoom", 1);
__decorateClass([
  Request2({
    path: "connect",
    method: "POST"
  })
], WorldRoom.prototype, "connect", 1);
WorldRoom = __decorateClass([
  Room({
    path: "world-{worldId}",
    maxUsers: 100,
    // Limit for admin connections
    throttleStorage: 2e3,
    // Throttle storage updates (ms)
    throttleSync: 500
    // Throttle sync updates (ms)
  })
], WorldRoom);

// src/session.guard.ts
function getPrivateId(sender) {
  return sender.sessionId || sender.id;
}
function createRequireSessionGuard(storage) {
  return async (sender, value) => {
    if (!sender || !sender.id) {
      return false;
    }
    try {
      const session = await storage.get(`session:${getPrivateId(sender)}`);
      if (!session) {
        return false;
      }
      const typedSession = session;
      if (!typedSession.publicId) {
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error checking session in requireSession guard:", error);
      return false;
    }
  };
}
var requireSession = async (sender, value, room) => {
  if (!sender || !sender.id) {
    return false;
  }
  try {
    const session = await room.storage.get(`session:${getPrivateId(sender)}`);
    if (!session) {
      return false;
    }
    const typedSession = session;
    if (!typedSession.publicId) {
      return false;
    }
    return true;
  } catch (error) {
    console.error("Error checking session in requireSession guard:", error);
    return false;
  }
};
export {
  Action,
  ClientIo,
  Guard,
  MockConnection,
  Request2 as Request,
  Room,
  RoomGuard,
  Server,
  ServerIo,
  ServerResponse,
  Shard,
  UnhandledAction,
  WorldRoom,
  createRequireSessionGuard,
  request,
  requireSession,
  testRoom,
  tick
};
//# sourceMappingURL=index.js.map